import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ClipboardCheck, ChevronDown, ChevronUp, Bell, CheckCircle, MailWarning } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Dedicated inbox for weekly check-ins the coach hasn't reviewed yet, plus a
 * roster gap list of clients who owe this week's check-in. Pinned to the top
 * of the Coach Dashboard so nothing falls through the cracks.
 *
 * Endpoints:
 *   GET  /coaching/checkins/pending
 *   POST /coaching/checkins/{id}/mark-reviewed
 *   POST /coaching/clients/{id}/remind-checkin
 */
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function PendingCheckinsCard() {
  const [data, setData] = useState({ pending: [], roster_missing_checkins_this_week: [] });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [reminding, setReminding] = useState({});
  const [remindingAll, setRemindingAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/coaching/checkins/pending");
      setData(r.data || { pending: [], roster_missing_checkins_this_week: [] });
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Refresh when the coach tabs back so a check-in submitted seconds ago is
    // visible without a manual reload.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const poll = setInterval(load, 45000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(poll);
    };
  }, [load]);

  const markReviewed = async (id) => {
    try {
      await api.post(`/coaching/checkins/${id}/mark-reviewed`);
      setData((d) => ({ ...d, pending: d.pending.filter((c) => c.id !== id) }));
      toast.success("Marked reviewed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remind = async (clientId) => {
    setReminding((r) => ({ ...r, [clientId]: true }));
    try {
      const r = await api.post(`/coaching/clients/${clientId}/remind-checkin`);
      const ch = r.data?.channels || {};
      const parts = [];
      if (ch.notif) parts.push("in-app");
      if (ch.push) parts.push(`${ch.push} push`);
      if (ch.email) parts.push("email");
      toast.success(`Reminder sent · ${parts.join(", ") || "queued"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remind");
    } finally {
      setReminding((r) => ({ ...r, [clientId]: false }));
    }
  };

  const remindAll = async () => {
    const count = (data.roster_missing_checkins_this_week || []).length;
    if (!window.confirm(`Send a check-in reminder (in-app + push + email) to all ${count} clients missing this week?`)) return;
    setRemindingAll(true);
    try {
      const r = await api.post("/coaching/checkins/remind-all-missing");
      const { sent = 0, skipped = 0 } = r.data || {};
      toast.success(
        `Reminded ${sent} client${sent === 1 ? "" : "s"}` +
          (skipped ? ` · ${skipped} skipped (already nudged in the last 20h)` : ""),
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk reminder failed");
    } finally {
      setRemindingAll(false);
    }
  };

  if (loading) return null;
  const hasAnything =
    (data.pending || []).length > 0 ||
    (data.roster_missing_checkins_this_week || []).length > 0;
  if (!hasAnything) return null;

  return (
    <div className="obs-card mb-6" data-testid="pending-checkins-card">
      <div className="flex items-center gap-3 mb-4">
        <ClipboardCheck className="h-5 w-5 text-blaze" />
        <div className="flex-1 min-w-0">
          <div className="overline">Coach inbox</div>
          <h3 className="font-heading text-2xl uppercase">Weekly check-ins</h3>
        </div>
        {data.pending.length > 0 && (
          <span
            className="text-[10px] uppercase tracking-widest px-2 py-1 bg-blaze/20 border border-blaze/50 text-blaze rounded-sm"
            data-testid="pending-checkins-badge"
          >
            {data.pending.length} unread
          </span>
        )}
      </div>

      {data.pending.length > 0 && (
        <div className="space-y-2 mb-5">
          {data.pending.map((c) => {
            const isOpen = expandedId === c.id;
            return (
              <div
                key={c.id}
                data-testid={`pending-checkin-${c.id}`}
                className="border border-obsidian-600 rounded-sm overflow-hidden bg-obsidian-900"
              >
                <button
                  type="button"
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-obsidian-800 text-left"
                  onClick={() => setExpandedId(isOpen ? null : c.id)}
                >
                  <Bell className="h-4 w-4 text-blaze shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100 truncate">
                      <span className="font-medium">{c.client_name}</span>
                      <span className="text-zinc-500"> · {fmtDate(c.created_at)}</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      Energy {c.energy}/5
                      {c.adherence_pct != null ? ` · Adherence ${c.adherence_pct}%` : ""}
                      {c.weight_kg ? ` · ${c.weight_kg} kg` : ""}
                    </div>
                  </div>
                  {isOpen ? (
                    <ChevronUp className="h-4 w-4 text-zinc-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-zinc-400" />
                  )}
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-1 border-t border-obsidian-600 bg-obsidian-900/40 space-y-3">
                    {c.notes && (
                      <div>
                        <div className="overline mb-1">Notes</div>
                        <p className="text-sm text-zinc-300">{c.notes}</p>
                      </div>
                    )}
                    {c.answers && Object.keys(c.answers).length > 0 && (
                      <div>
                        <div className="overline mb-1">Answers</div>
                        <div className="space-y-2">
                          {Object.entries(c.answers).map(([q, a]) => (
                            <div key={q} className="text-sm">
                              <div className="text-zinc-500 text-[11px] uppercase tracking-widest">{q}</div>
                              <div className="text-zinc-200">{a || <span className="italic text-zinc-500">— no answer —</span>}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => markReviewed(c.id)}
                        data-testid={`mark-reviewed-${c.id}`}
                        className="ghost-btn px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5"
                      >
                        <CheckCircle className="h-3 w-3" /> Mark reviewed
                      </button>
                      <Link
                        to={`/coach-dashboard/clients/${c.user_id}`}
                        className="ghost-btn px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5"
                      >
                        Open client profile →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(data.roster_missing_checkins_this_week || []).length > 0 && (
        <div>
          <div className="overline mb-2 flex items-center gap-2">
            <MailWarning className="h-3 w-3 text-yellow-500" />
            Missing this week ({data.roster_missing_checkins_this_week.length})
            <button
              type="button"
              onClick={remindAll}
              disabled={remindingAll}
              data-testid="remind-all-missing-btn"
              className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 border border-blaze/40 text-blaze hover:bg-blaze/10 rounded-sm transition-colors disabled:opacity-50"
            >
              <Bell className="h-3 w-3" />
              {remindingAll ? "Sending…" : "Remind all"}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.roster_missing_checkins_this_week.map((c) => (
              <div
                key={c.id}
                data-testid={`missing-checkin-${c.id}`}
                className="flex items-center gap-2 px-3 py-1.5 border border-obsidian-600 rounded-sm bg-obsidian-900"
              >
                <span className="text-sm text-zinc-200 truncate max-w-[140px]">
                  {c.first_name || (c.email || "").split("@")[0]}
                </span>
                <button
                  type="button"
                  onClick={() => remind(c.id)}
                  disabled={!!reminding[c.id]}
                  data-testid={`remind-checkin-${c.id}`}
                  className="text-[10px] uppercase tracking-widest text-blaze hover:underline disabled:opacity-50"
                >
                  {reminding[c.id] ? "Sending…" : "Remind"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
